import { GenericAbortSignal } from 'axios';
import { format } from 'date-fns';

import { NONE, PHYSICIAN_TEAM } from 'constants/filterKeysConstants';
import FilterValueArray from 'models/filterValues/FilterValueArray';
import FilterValueBoolean from 'models/filterValues/FilterValueBoolean';
import LocationEpisode, { LocationEpisodeOptions } from 'models/LocationEpisode';
import { Paginated, PaginationMeta } from 'models/Paginated';
import http from 'services/http';
import { DischargeCalendarFilterState } from 'stores/dischargeCalendarStore';

import { BaseIndexQueryParams } from './types';

type DischargeCalendarApiResponse = Paginated<LocationEpisodeOptions>;

type DischargeCalendarParams = {
  startDate: Date;
  endDate: Date;
  filters?: DischargeCalendarFilterState;
  search?: string;
} & BaseIndexQueryParams;

export type DayData = Paginated<LocationEpisode>;

export type WeekViewApiResponse = Record<string, Paginated<LocationEpisodeOptions>>;

export type WeekViewData = Record<string, Paginated<LocationEpisode>>;

function serializeFilters(filters: DischargeCalendarFilterState) {
  return Object.entries(filters).reduce(
    (acc, [key, value]) => {
      if (value instanceof FilterValueArray) {
        if (!value || value.length === 0) return acc;

        if (key === PHYSICIAN_TEAM) {
          acc[key] = value.map((x) => (x.name === NONE ? x.id : x.name));
        } else {
          acc[key] = value.map((x) => x.id).join(',');
        }
      } else if (value instanceof FilterValueBoolean) {
        if (value.value) {
          acc[key] = value.value;
        }
      }
      return acc;
    },
    {} as Record<string, any>
  );
}

export async function indexDischargeCalendar(
  { startDate, endDate, filters, search, ...params }: DischargeCalendarParams,
  signal: GenericAbortSignal
) {
  const serializedParams = {
    ...params,
    ...serializeFilters(filters ?? {}),
    search,
    projectedDischargeDateRange: [
      format(startDate, 'yyyy-MM-dd') || new Date('1900-01-01'),
      format(endDate, 'yyyy-MM-dd') || new Date('2999-01-01'),
    ],
    include: 'episode,plan_type_classification,reviews.group,group.group_type',
    active: true,
  };

  return http
    .get<Paginated<LocationEpisodeOptions>>('/location_episodes/discharge_calendar', {
      params: serializedParams,
      signal,
    })
    .then((res) => ({
      ...res.data,
      data: res.data.data.map((entry) => new LocationEpisode(entry)),
    }));
}

export type DischargeCalendarCounts = Record<string, { medicare: number; other: number }>;

type DischargeCalendarCountsResponse = {
  data: DischargeCalendarCounts;
};

type DischargeCalendarCountsParams = {
  startDate: Date;
  endDate: Date;
  filters?: DischargeCalendarFilterState;
  search?: string;
};

export async function getDischargeCalendarCounts(
  { startDate, endDate, filters, search }: DischargeCalendarCountsParams,
  signal: GenericAbortSignal
) {
  const params = {
    ...serializeFilters(filters ?? {}),
    search,
    projectedDischargeDateRange: [format(startDate, 'yyyy-MM-dd'), format(endDate, 'yyyy-MM-dd')],
  };

  return http
    .get<DischargeCalendarCountsResponse>('/location_episodes/discharge_calendar/counts', {
      params,
      signal,
    })
    .then((res) => res.data.data);
}

export async function indexDischargeCalendarWeek(
  { startDate, endDate, filters, search }: DischargeCalendarParams,
  signal: GenericAbortSignal
): Promise<WeekViewData> {
  const serializedParams = {
    ...serializeFilters(filters ?? {}),
    search,
    projectedDischargeDateRange: [format(startDate, 'yyyy-MM-dd'), format(endDate, 'yyyy-MM-dd')],
    include: 'episode.patient,reviews.group,group.group_type',
    active: true,
  };

  return http
    .get<WeekViewApiResponse>('/location_episodes/discharge_calendar/week', {
      params: serializedParams,
      signal,
    })
    .then((res) => {
      const transformed: WeekViewData = {};
      Object.entries(res.data).forEach(([dateKey, dayData]) => {
        transformed[dateKey] = {
          ...dayData,
          data: dayData.data.map((entry) => new LocationEpisode(entry)),
        };
      });
      return transformed;
    });
}

type DayPaginationParams = {
  date: Date;
  page: number;
  filters?: DischargeCalendarFilterState;
  search?: string;
};

export type DayPaginationResponse = {
  data: LocationEpisode[];
  meta: PaginationMeta;
};

export async function indexDischargeCalendarDay(
  { date, page, filters, search }: DayPaginationParams,
  signal: GenericAbortSignal
): Promise<DayPaginationResponse> {
  const dateString = format(date, 'yyyy-MM-dd');
  const serializedParams = {
    ...serializeFilters(filters ?? {}),
    search,
    projectedDischargeDateRange: [dateString, dateString],
    page,
    pageSize: 25,
    include: 'episode.patient,reviews.group.group_type,group.group_type',
    active: true,
    sortBy: 'medicare asc,patient_name asc',
  };

  return http
    .get<DischargeCalendarApiResponse>('/location_episodes/discharge_calendar', {
      params: serializedParams,
      signal,
    })
    .then((res) => {
      return {
        ...res.data,
        data: res.data.data.map((le) => new LocationEpisode(le)),
      };
    });
}

export const dischargeCalendarQueryKeys = {
  base: ['dischargeCalendar'],
  index: (params: Record<string, any> = {}) => ['dischargeCalendar', 'index', params],
  week: (params: Record<string, any> = {}) => ['dischargeCalendar', 'week', params],
  day: (date: string, params: Record<string, any> = {}) => ['dischargeCalendar', 'day', date, params],
  counts: (params: Record<string, any> = {}) => ['dischargeCalendar', 'counts', params],
};
